export type VendorTier =
  | 'tier_1_critical'
  | 'tier_2_high'
  | 'tier_3_medium'
  | 'tier_4_low';

export type VendorStatus =
  | 'prospect'
  | 'onboarding'
  | 'active'
  | 'under_review'
  | 'suspended'
  | 'offboarded';

export interface Vendor {
  id: string;
  tenant_id: string;
  parent_vendor_id: string | null;
  name: string;
  trading_name: string | null;
  vendor_type: string;
  tier: VendorTier;
  status: VendorStatus;
  industry: string | null;
  country: string | null;
  website: string | null;
  description: string | null;
  services_provided: string[];
  data_access_level: string;
  criticality_score: number;
  inherent_risk_score: number | null;
  assessment_score: number | null;
  last_assessed_at: string | null;
  next_assessment_due: string | null;
  // Slice 3 — additive list-computed columns (honest table; replace composite/rating)
  open_findings_count?: number;
  open_findings_critical?: number;
  earliest_overdue_due?: string | null;
  retired_at?: string | null; // audit vendor.retired occurred_at (retired table only); null → "—"
  contract_start_date: string | null;
  contract_end_date: string | null;
  contract_value_usd: number | null;
  primary_contact_id: string | null;
  assigned_analyst_id: string | null;
  lit_hold_active: boolean;
  tags: string[];
  metadata: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface VendorContact {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  contact_role: string;
  is_primary: boolean;
  is_portal_admin: boolean;
  invitation_status: string | null;
  invitation_sent_at: string | null;
  invitation_accepted_at: string | null;
  phone: string | null;
  title: string | null;
  created_at: string;
}

export interface PortalContact {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role_title: string | null;
  status: 'pending' | 'active' | 'removed' | 'lapsed' | 'disabled';
  invitation_sent_at: string | null;
  accepted_at: string | null;
  assessment_count: number;
  last_login_at: string | null;
  created_at: string;
}

export interface CreateVendorDto {
  name: string;
  trading_name?: string;
  tier?: VendorTier;
  status?: VendorStatus;
  industry?: string;
  country?: string;
  website?: string;
  description?: string;
  services_provided?: string[];
  data_access_level?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface VendorFilters {
  tier?: string;
  status?: string;
  search?: string;
  // Slice 3 — additive BI segment filters (URL-backed)
  tiers?: string;
  overdue?: string;
  has_findings?: string;
  finding_severity?: string;
  cert_expiring?: string;
  ra_open?: string;
  outcome?: string;
  active_only?: string;
}

export interface VendorListMeta {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export const TIER_LABELS: Record<VendorTier, string> = {
  tier_1_critical: 'Tier 1 — Critical',
  tier_2_high: 'Tier 2 — High',
  tier_3_medium: 'Tier 3 — Medium',
  tier_4_low: 'Tier 4 — Low',
};

export const TIER_SHORT_LABELS: Record<VendorTier, string> = {
  tier_1_critical: 'Tier 1',
  tier_2_high: 'Tier 2',
  tier_3_medium: 'Tier 3',
  tier_4_low: 'Tier 4',
};
